import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { updateSystemSetting } from "@/api/lookups";
import type { SystemSettingResponse } from "@/api/types";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const PREDEFINED_SETTING_KEYS = [
  "upload_max_bytes",
  "upload_max_duration_seconds",
  "upload_allowed_extensions",
  "retention_days",
] as const;

const schema = z
  .object({
    key: z.string().min(1).max(64),
    value: z.string().min(1),
  })
  .superRefine((val, ctx) => {
    try {
      const parsed = JSON.parse(val.value);
      if (
        typeof parsed !== "object" ||
        parsed === null ||
        Array.isArray(parsed)
      ) {
        ctx.addIssue({
          path: ["value"],
          code: z.ZodIssueCode.custom,
          message: "valueInvalidJson",
        });
      }
    } catch {
      ctx.addIssue({
        path: ["value"],
        code: z.ZodIssueCode.custom,
        message: "valueInvalidJson",
      });
    }
  });

type FormValues = z.infer<typeof schema>;

export interface SettingFormProps {
  open: boolean;
  existing: SystemSettingResponse | null;
  onClose: () => void;
  afterSubmit: () => Promise<unknown> | void;
}

export function SettingForm({
  open,
  existing,
  onClose,
  afterSubmit,
}: SettingFormProps) {
  const { t } = useTranslation();
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { key: "", value: "{}" },
    values:
      existing === null
        ? undefined
        : {
            key: existing.key,
            value: JSON.stringify(existing.value, null, 2),
          },
  });
  const mutation = useMutation({
    mutationFn: (v: FormValues) =>
      updateSystemSetting({
        key: v.key,
        value: JSON.parse(v.value) as Record<string, unknown>,
      }),
    onSuccess: async () => {
      await afterSubmit();
      onClose();
    },
  });

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? undefined : onClose())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {existing === null ? t("common.create") : t("common.edit")}
          </DialogTitle>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={form.handleSubmit((v) => mutation.mutate(v))}
          data-testid="setting-form"
        >
          <div>
            <Label htmlFor="setting-key">{t("adminSettings.fields.key")}</Label>
            {existing === null ? (
              <select
                id="setting-key"
                className="h-9 w-full rounded-md border bg-background px-2 text-sm"
                {...form.register("key")}
              >
                <option value="">{t("common.dash")}</option>
                {PREDEFINED_SETTING_KEYS.map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </select>
            ) : (
              <Input id="setting-key" {...form.register("key")} readOnly />
            )}
            {form.formState.errors.key !== undefined ? (
              <p className="text-xs text-destructive">
                {t("adminSettings.errors.keyRequired")}
              </p>
            ) : null}
          </div>
          <div>
            <Label htmlFor="setting-value">
              {t("adminSettings.fields.value")}
            </Label>
            <textarea
              id="setting-value"
              className="min-h-32 w-full rounded-md border bg-transparent px-3 py-2 text-sm"
              {...form.register("value")}
            />
            {form.formState.errors.value !== undefined ? (
              <p className="text-xs text-destructive">
                {t("adminSettings.errors.valueInvalidJson")}
              </p>
            ) : null}
          </div>
          {mutation.isError ? (
            <p className="text-xs text-destructive" role="alert">
              {t("adminSettings.errors.saveFailed")}
            </p>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? t("common.saving") : t("common.save")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
